import { organizationEnhancedDynamicFieldsAvailableQueryOptions } from '@/api/dynamicFieldsQueryOptions'
import { FilterParam, SortParam, VisibilityParam } from '@/api/memberships'
import { OrganizationLevelType } from '@/api/organizations'
import { ApiErrorComponent } from '@/components/ApiErrorComponent'
import useResponsive from '@/components/hooks/useResponsive'
import LoadProgress from '@/components/LoadProgress'
import { getOrganization } from '@/store/auth/authSlice'
import MembershipsSidebar from '@/ui/memberships/MembershipsSidebar'
import MembershipsTable from '@/ui/memberships/MembershipsTable'
import { Box } from '@mui/material'
import { GridColumnVisibilityModel, GridFilterItem, GridFilterModel, GridLogicOperator, GridSortModel, useGridApiRef } from '@mui/x-data-grid-premium'
import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useSelector } from 'react-redux'

type MembershipsSearch = {
    filter: FilterParam[],
    sort: SortParam[],
    visibility: VisibilityParam[] | undefined,
    forceFilter?: boolean,
}

export const Route = createFileRoute('/_auth/membershipsOverview')({
    validateSearch: (search: Record<string, unknown>): MembershipsSearch => {
        return {
            filter: search.filter as FilterParam[] || [],
            visibility: search.visibility as VisibilityParam[] || [],
            sort: search.sort as SortParam[] || [],
            forceFilter: search.forceFilter as boolean || false,
        }
    },
    component: MembershipsComponent,
    errorComponent: ({ error }) => {
        if (error) {
            return <ApiErrorComponent error={error as Error} />
        } else {
            return <ApiErrorComponent />
        }
    },
})

function MembershipsComponent() {
    const { filter, sort, visibility, forceFilter } = Route.useSearch()

    const responsive = useResponsive()
    const sessionOrganization = useSelector(getOrganization)

    const enhancedDynamicFieldsQuery = useSuspenseQuery(organizationEnhancedDynamicFieldsAvailableQueryOptions(sessionOrganization.uuid))
    const enhancedDynamicFields = enhancedDynamicFieldsQuery.data;

    const initialFilterModel: GridFilterModel = {
        items: filter.map((filter) => {
            if ((filter.field.startsWith('properties.joblist')
                || filter.field.startsWith('properties.section')
                || filter.field.startsWith('scoutstate')) && filter.value instanceof String) {
                return {
                    id: filter.field,
                    field: filter.field,
                    operator: filter.operator,
                    value: filter.value.split(',')
                } as GridFilterItem
            }
            return {
                id: filter.field,
                field: filter.field,
                operator: filter.operator,
                value: filter.value
            } as GridFilterItem
        }),
        logicOperator: GridLogicOperator.And,
    }

    if (forceFilter) {
        localStorage.removeItem('scoreg.gridFilterModel.membership')
    }

    const initialSortModel = sort.map((sort) => {
        return {
            field: sort.field,
            sort: sort.direction
        }
    }) as GridSortModel;

    let initialVisibilityModel = {} as GridColumnVisibilityModel;
    if (visibility) {
        initialVisibilityModel = {
            'properties.scoutstate': false,
            'properties.joblist': false,
            'properties.section': false,
            'properties.paymentTroop': false,
            'properties.paymentDivision': false,
            'properties.paymentFederation': false,
            'properties.paymentTroopFullpayer': false,
            'properties.registrationStatus': false,
            'properties.registrationOrganization': false,
            'member.street': false,
            'member.postcode': false,
            'member.city': false,
            'member.addressaddition1': false,
            'member.addressaddition2': false,
            'organization.name': sessionOrganization.organizationLevel != OrganizationLevelType.TROOP,
            'contacts.contact1Type': false,
            'contacts.contact1Name': false,
            'contacts.contact1Email': false,
            'contacts.contact1Phone': false,
            'contacts.contact1Address': false,
            'contacts.contact1Profession': false,
            'contacts.contact2Type': false,
            'contacts.contact2Name': false,
            'contacts.contact2Email': false,
            'contacts.contact2Phone': false,
            'contacts.contact2Address': false,
            'contacts.contact2Profession': false,
            'dynamicFields.dynamicField1': false,
            'dynamicFields.dynamicField2': false,
            'dynamicFields.dynamicField3': false,
            'dynamicFields.dynamicField4': false,
            'dynamicFields.dynamicField5': false,
            'dynamicFields.checkField1': false,
            'dynamicFields.checkField2': false,
            'dynamicFields.checkField3': false,
            'dynamicFields.checkField4': false,
            'dynamicFields.checkField5': false,
            'dynamicFields.dateField1': false,
            'dynamicFields.dateField2': false,
            'dynamicFields.dateField3': false,
            'dynamicFields.dateField4': false,
            'dynamicFields.dateField5': false,
            'properties.listHeader': false,
            'listMember.text1': false,
            'listMember.text2': false,
            'listMember.date1': false,
            'listMember.date2': false,
            'listMember.num1': false,
            'listMember.num2': false,
            'listMember.check1': false,
            'listMember.check2': false,
            'division.uuid': false,
            'federation.uuid': false,

        } as GridColumnVisibilityModel

        if (!responsive.minMedium) {
            initialVisibilityModel = { ...initialVisibilityModel, 'member.scoutId': false }
            initialVisibilityModel = { ...initialVisibilityModel, 'member.birthdate': false }
            initialVisibilityModel = { ...initialVisibilityModel, 'member.firstname': false }
            initialVisibilityModel = { ...initialVisibilityModel, 'member.lastname': false }
            initialVisibilityModel = { ...initialVisibilityModel, 'member.sex': false }
            initialVisibilityModel = { ...initialVisibilityModel, 'member.name': true }
        } else {
            initialVisibilityModel = { ...initialVisibilityModel, 'member.name': false }
        }
        if (enhancedDynamicFields) {
            enhancedDynamicFields.forEach((dynamicField) => {
                initialVisibilityModel = { ...initialVisibilityModel, ['enhancedDynamicField.' + dynamicField.uuid]: false };
            });
            // console.log(initialVisibilityModel);
        }

        if (visibility) {
            visibility.map((visibility) => {
                initialVisibilityModel = { ...initialVisibilityModel, [visibility.field]: true }
            });
        }
    }

    const apiRef = useGridApiRef()

    if (apiRef == null) {
        return <LoadProgress />
    }

    return (
        <>
            <Box sx={{
                height: { xs: `calc(100% - 5px)` }
            }}>
                <Box sx={{
                    alignContent: 'left'
                }}>
                    <MembershipsSidebar apiRef={apiRef} />
                </Box>
                <Box
                    sx={{
                        width: { xs: '100%', md: `calc(100% - 120px)` },
                        height: { xs: '100%' }
                    }}>
                    <MembershipsTable
                        apiRef={apiRef}
                        initialFilterModel={initialFilterModel}
                        initialSortModel={initialSortModel}
                        initialVisibilityModel={initialVisibilityModel}
                    />
                </Box>
            </Box>
        </>
    );
}
